//  Updated App.js with support for both Auth0 and Okta role checking
import 'core-js/stable'
import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'

import {useFlags, useLDClient} from 'launchdarkly-react-client-sdk'
import * as Sentry from '@sentry/react'
import AuthenticatedApp from './authenticated-app'
import UnauthenticatedApp from './unauthenticated-app'
// import {Header} from './components/header'
import {AppHeader} from './components/app-shell/AppHeader'
import {AppBackground} from './components/app-shell/AppBackground'
import {GlobalStyles} from './core/theme/Globalstyles'
import {
  AppFooter,
  AppFooterLayout,
  AppPageContent,
} from './components/app-shell/AppFooter'
import {AppContainer, OldAppContainer} from './components/ui/app'
import {AppSpinner} from './components/loader-spinner'
import {HeaderProvider} from './context/header-context'
import {
  InitDataDogRum,
  DataDogStartSession,
  // DataDogEndSession,
} from './dataDogConfig'
// import {useAsync} from './utils/use-async'
import {useAuth0} from '@auth0/auth0-react'
import {useAuthClient} from './utils/use-auth-client'
import {ToastContainer, toast} from 'react-toastify'
// import {SideNavBar} from './components/side-navbar'
import {SideNav} from './components/side-nav/side-nav'
import {KeapPartnerSideNav} from './components/side-nav/keap-partner-side-nav'
import AuthenticatedKeapPartnerApp from './authenticated-kp-app'
import {HeaderKeapPartner} from './components/header-keap-partner'
// import {getUserRoles, hasAnyRole} from './utils/get-user-roles'
import {hasAnyRole} from './utils/get-user-roles'
import {useSelector} from 'react-redux'
import {selectEffectiveRole, selectUserData} from './app-store/selector'

function Container({
  children,
  frontendSideNav,
  isSidebarOpen,
  isMobileSidebarOpen,
}) {
  if (frontendSideNav) {
    return (
      <AppContainer
        id="app-container"
        isSidebarOpen={isSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
      >
        <GlobalStyles />
        <AppBackground />
        {children}
      </AppContainer>
    )
  }
  return (
    <OldAppContainer>
      <GlobalStyles />
      {children}
    </OldAppContainer>
  )
}

function ContainerKeapPartner({
  children,
  isSidebarOpen,
  isMobileSidebarOpen,
  hideSidebar,
}) {
  React.useEffect(() => {
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = 'auto' // reset on unmount
    }
  }, [])
  // Same sidebar grid as the core OOE shell.
  return (
    <AppContainer
      id="app-kp-container"
      hideSidebar={hideSidebar}
      isSidebarOpen={isSidebarOpen}
      isMobileSidebarOpen={isMobileSidebarOpen}
    >
      <GlobalStyles />
      <AppBackground />
      {children}
    </AppContainer>
  )
}

function App() {
  // const {isLoading, isAuthenticated, error, user} = useAuth0()
  const {loginWithRedirect} = useAuth0()
  const LDClient = useLDClient()
  const {pathname} = useLocation()
  const isPaymentInfo = pathname.includes('payment-information')
  const isScanResults = pathname.includes('scan-results')
  const isProposalView =
    pathname.startsWith('/proposal-view/') ||
    pathname.startsWith('/proposalLink/')
  const randomId = pathname.split('/')[2]
  // const [versionHash, setVersionHash] = React.useState(null)
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false)
  // const {run} = useAsync()
  const client = useAuthClient()
  const {frontendSideNav} = useFlags()

  const {isLoading, isAuthenticated, error, user} = useSelector(selectUserData)
  const userRoles = useSelector(selectEffectiveRole)
  // Get user roles using the new utility that supports both Auth0 and Okta
  // const userRoles = getUserRoles(user)

  console.log('isLoading - App.js', isLoading)
  console.log('isAuthenticated - App.js', isAuthenticated)
  console.log('user - App.js', user)
  console.log('userRoles - App.js', userRoles)
  // if (user?.user_roles !== undefined) {
  //   user.user_roles = ['InfluencerOps']
  // }

  const standaloneRoutes = []
  const isStandaloneRoute = standaloneRoutes.some(r => pathname.startsWith(r))

  const [userData, setUserData] = useState(null)
  const [rolesLoaded, setRolesLoaded] = useState(false)

  const fetchUserData = async () => {
    try {
      const url = 'user?email=' + user?.email
      const data = await client(url)
      if (data) {
        const enriched = Array.isArray(data)
          ? data.map(d => ({...d, user_roles: userRoles}))
          : {...data, user_roles: userRoles}

        setUserData(enriched)
      }
    } catch (e) {
      toast.error('Failed to fetch profile data')
    } finally {
      setRolesLoaded(true)
    }
  }

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user])
  React.useEffect(() => {
    if (LDClient) {
      LDClient.on('error', err => {
        console.warn('LaunchDarkly error:', err?.message || err)
      })
    }
    return () => {
      if (LDClient) {
        LDClient.off('error')
      }
    }
  }, [LDClient])

  React.useEffect(() => {
    if (isAuthenticated) {
      LDClient.identify({
        kind: 'user',
        key: user.email,
        email: user.email,
        name: user.name,
        roles: userRoles,
      })
      Sentry.setUser(user)
    }
  }, [LDClient, isAuthenticated, user, userRoles])

  React.useEffect(() => {
    if (
      isAuthenticated ||
      (isPaymentInfo && randomId) ||
      isScanResults ||
      isProposalView
    ) {
      if (process.env.NODE_ENV !== 'development') {
        if (user) {
          InitDataDogRum({name: user.name, email: user.email})
        } else if (isPaymentInfo && randomId) {
          InitDataDogRum({name: randomId, email: ''})
        } else if (isProposalView && randomId) {
          InitDataDogRum({name: randomId, email: ''})
        }
      }
    }
  }, [
    isAuthenticated,
    isPaymentInfo,
    isScanResults,
    isProposalView,
    randomId,
    user,
  ])

  React.useEffect(() => {
    // DataDogEndSession()
    if (
      (isAuthenticated ||
        (isPaymentInfo && randomId) ||
        isScanResults ||
        isProposalView) &&
      !pathname.includes('tools')
    ) {
      if (user || (isPaymentInfo && randomId) || isProposalView) {
        DataDogStartSession()
      }
    }
  }, [
    isAuthenticated,
    isPaymentInfo,
    isScanResults,
    isProposalView,
    randomId,
    user,
    pathname,
  ])

  React.useEffect(() => {
    if (isAuthenticated && window.pendo) {
      window.pendo.initialize({
        visitor: {
          id: user.email,
          full_name: user.name,
          role: userRoles,
        },
        account: {
          id: user.email,
          name: user.name,
        },
      })
    }
  }, [isAuthenticated, user, userRoles])

  // COMMENTED OUT: Backend /api/user/hash endpoint returns empty response causing JSON parse error
  // React.useEffect(() => {
  //   console.log('is it triggering process env v1')
  //   if (!isAuthenticated) return

  //   let timeout

  //   const getAppVersion = async () => {
  //     const response = await run(client('user/hash'))
  //     console.log(versionHash, response)
  //     if (versionHash && versionHash !== response) {
  //       toast.warn(
  //         'App has been updated, please refresh to get the latest version',
  //         {autoClose: false},
  //       )
  //       return
  //     }
  //     if (!versionHash) {
  //       setVersionHash(response)
  //       console.log(response)
  //     }
  //     timeout = setTimeout(getAppVersion, 300000)
  //   }
  //   getAppVersion()

  //   return () => {
  //     console.log('moved out of the previous data')
  //     clearTimeout(timeout)
  //   }
  // }, [isAuthenticated, client, run, versionHash])

  if (isProposalView) {
    return (
      <AppFooterLayout>
        <UnauthenticatedApp />
      </AppFooterLayout>
    )
  }

  if (isLoading) {
    return <AppSpinner />
  }
  if (error) {
    const isPublicPage = isPaymentInfo || isScanResults || isProposalView
    if (
      !isPublicPage &&
      (error.error === 'login_required' || error.message === 'Login required')
    ) {
      loginWithRedirect({
        appState: {returnTo: window.location.pathname},
      })
      return <AppSpinner />
    }
    if (!isPublicPage) {
      return <div>Oops... {error.message}</div>
    }
  }

  // Wait for roles to be loaded before determining which app to render
  if (isAuthenticated && !rolesLoaded) {
    return <AppSpinner />
  }

  // Check if user has InfluencerOps or KeapPartner role using the new utility
  const isKeapPartnerUser = hasAnyRole(user, ['InfluencerOps', 'KeapPartner'])

  if (isAuthenticated && !isKeapPartnerUser && isStandaloneRoute) {
    return (
      <>
        <main style={{height: '100vh', overflow: 'hidden'}}>
          <AuthenticatedApp />
        </main>
        <ToastContainer />
      </>
    )
  }

  if (isAuthenticated && !isKeapPartnerUser) {
    return (
      <Container
        frontendSideNav={frontendSideNav}
        isSidebarOpen={isSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
      >
        <HeaderProvider>
          {/* {frontendSideNav ? ( */}
          <AppHeader
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isSidebarOpen={isSidebarOpen}
          />
          {/* ) : (
            <Header />
          )} */}
          {frontendSideNav && (
            // <SideNavBar
            //   isSidebarOpen={isSidebarOpen}
            //   setIsSidebarOpen={setIsSidebarOpen}
            //   isMobileSidebarOpen={isMobileSidebarOpen}
            //   setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            // />
            <SideNav
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              isMobileSidebarOpen={isMobileSidebarOpen}
              setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            />
          )}
          <main>
            <AppPageContent>
              <AuthenticatedApp />
            </AppPageContent>
            <AppFooter />
          </main>
        </HeaderProvider>
        <ToastContainer />
      </Container>
    )
  }

  if (isAuthenticated && isKeapPartnerUser) {
    return (
      <ContainerKeapPartner
        hideSidebar={pathname === '/'}
        isSidebarOpen={isSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
      >
        <HeaderProvider>
          <HeaderKeapPartner
            userData={userData}
            isSidebarOpen={isSidebarOpen}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          />
          <KeapPartnerSideNav
            userData={userData}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          />
          <main>
            <AppPageContent
              edgeToEdge={pathname === '/'}
              topGap={pathname === '/' ? undefined : '16px'}
            >
              <AuthenticatedKeapPartnerApp userData={userData} />
            </AppPageContent>
            <AppFooter />
          </main>
        </HeaderProvider>
        <ToastContainer />
      </ContainerKeapPartner>
    )
  }

  return (
    <AppFooterLayout>
      <UnauthenticatedApp />
    </AppFooterLayout>
  )
}

export {App}
